import { inject, Injectable } from '@angular/core';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';

import { AppActions, CheckoutActions, CpqActions, CustomerActions } from '../actions';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { MarketingFacade } from '../facades/marketing.facade';
import { CheckoutFacade } from '../facades/checkout.facade';
import * as OrderApproveActions from '../actions/order-approve.actions';
import { CustomerFacade } from '../facades/customer.facade';
import { LocalStorageUtils } from '../utils/localStorage.utils';
import { I18nService } from '../services';

@Injectable()
export class AppEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly checkoutFacade: CheckoutFacade = inject(CheckoutFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly router: Router = inject(Router);
  private readonly i18nService: I18nService = inject(I18nService);

  readonly GetCompanyRoles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.CompanyUsersActionStart),
      mergeMap(
        () => this.customerFacade.getCompanyUsers()
          .pipe(
            map(data => {
              return CustomerActions.CompanyUsersActionSuccess({payload: data});
            }),
            catchError(error => of(CustomerActions.CompanyUsersActionError(error)),
            ),
          ),
      ),
    ),
  );

  readonly GetApprovers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.BeginApproversListAction),
      mergeMap(
        () => this.checkoutFacade.getCompanyUsers()
          .pipe(
            map(data => {
              return CheckoutActions.SuccessGetApproversListAction({payload: data});
            }),
            catchError(error => of(CheckoutActions.ErrorGetApproversListAction(error))),
          ),
      ),
    ),
  );

  readonly loadCartItemsForOrderApprove$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApproveActions.loadCartItems),
      mergeMap(action => {
        if (action.cartId === '') {
          return this.configurationFacade.getTranslationByKey('error.valid-cart-id').pipe(
            map(message =>
              OrderApproveActions.loadCartItemsFail({
                category: 'error',
                message,
                action: OrderApproveActions.loadCartItemsReset.type,
              }),
            ),
          );
        } else {
          return this.marketingFacade.getCartItems(action.cartId)
            .pipe(
              map(cartItems => OrderApproveActions.loadCartItemsSuccess({cartItems})),
              catchError(error => of(OrderApproveActions.loadCartItemsFail({
                category: 'error',
                message: error,
                action: OrderApproveActions.loadCartItemsReset.type,
              }))),
            );
        }
      }));
  });

  readonly loadCartItemsForOrderApproveFail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApproveActions.loadCartItemsFail),
      map(error =>
        AppActions.setAlert({
          alert: {
            type: error.category,
            message: error.message,
            action: error.action,
          },
        }),
      ));
  });

  readonly loadCustomerData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.loadCustomerData),
      mergeMap(
        () => this.customerFacade.getCompanyUsers()
          .pipe(
            map(data => CustomerActions.CompanyUsersActionSuccess({payload: data})),
            catchError(error => of(CustomerActions.loadCustomerDataError({error}))),
          ),
      ),
    );
  });

  readonly redirectToUrl$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.redirectToUrl),
      exhaustMap((action) => {
        if (!action?.url?.length) {
          return of(AppActions.redirectToUrlSkipped());
        }
        return of(AppActions.redirectToUrlExecuted({url: action.url, replaceUrl: action.replaceUrl}));
      }));
  });

  readonly redirectToUrlExecuted$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.redirectToUrlExecuted),
        tap(({url, replaceUrl}) => this.router.navigateByUrl(url, {replaceUrl})),
      ),
    {dispatch: false},
  );

  readonly redirectToLastPage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.redirectToLastPage),
      exhaustMap(() => {
        const homepageUrl: string = `/${this.i18nService.getCurrentLocale()}`;
        const previousPage: string = LocalStorageUtils.getKeyValue('previousPage') ?? homepageUrl;
        const currentRoute: string = this.router.url;
        const url: string = previousPage === currentRoute ? homepageUrl : previousPage;
        return of(AppActions.redirectToUrl({url, replaceUrl: true}));
      }));
  });

  readonly redirectToHomepage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.redirectToHomepage, CpqActions.serviceConfigPageRedirectWhenCartEmpty),
      exhaustMap(() => {
        const url: string = `/${this.i18nService.getCurrentLocale()}`;
        return of(AppActions.redirectToUrl({url, replaceUrl: true}));
      }));
  });

  readonly redirectToShopCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppActions.redirectToShopCart),
      exhaustMap(() => {
        const url: string = `/${this.i18nService.getCurrentLocale()}/shop-cart`;
        return of(AppActions.redirectToUrl({url, replaceUrl: true}));
      }));
  });
}
